import React, { useEffect, useRef, useState } from "react";
import Isotope from "isotope-layout";
import ProjectDetailsModal from "./ProjectDetailsModal";

const Portfolio = () => {
  const isotope = useRef();
  const [filterKey, setFilterKey] = useState("*");
  const [imagesLoaded, setimagesLoaded] = useState(0);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState();
  const [isOpen, setIsOpen] = useState(false);

  const htmlElement = document.getElementsByTagName("html")[0];
  const isRtl = htmlElement.getAttribute("dir") === "rtl";

  const filters = {
    CASESTUDY: "Case Studies",
    DETAILED: "Details",
    MOCKUPS: "Mockups",
    YOUTUBE: "Youtube Videos",
    VIMEO: "Vimeo Videos",
  };

  const types = {
    IMAGE: "image",
    VIDEO: "video",
    DOCUMENT: "document",
    CASESTUDY: "casestudy",
  };

  const projectsData = [
    {
      title: "International Experience Program",
      subtitle: "Higher Education Institution, Toronto · UX Design & Technical Architecture",
      type: types.CASESTUDY,
      thumbImage: "images/projects/project-1.jpg",
      categories: [filters.CASESTUDY],
      caseStudy: {
        client: "Higher Education Institution, Toronto (NDA)",
        role: "Full Stack Engineer & UX Designer",
        technologies: "PHP · Laravel · MySQL · JavaScript · React · LAMP Stack",
        date: "December 2023",
        brief:
          "A faculty-facing web application allowing staff to apply for funding to take students on international experiences as part of their curriculum. Submissions are capped per academic session and route through a hierarchical approval workflow spanning multiple disciplines and stakeholder roles — from faculty applicant through departmental and institutional sign-off.",
        goal:
          "Demonstrate a methodological approach to transforming business requirements into technical specifications — covering database architecture, process decomposition, UX wireframes, and acceptance criteria — for both front-end and backend layers.",
        phases: [
          {
            number: "01",
            title: "Process Decomposition",
            description:
              "Before writing a single line of code, the business process was broken into distinct, named stages. Each stage maps to a responsible actor, a status, and a set of transitions. This stage diagram became the backbone of both the database schema and the UI workflow.",
            icon: "fas fa-sitemap",
          },
          {
            number: "02",
            title: "Database Architecture — 19-Entity ERD",
            description:
              "A relational model was designed around 19 entities including Application, Users, Stages, Budget, Itinerary, Course, Program, and more. Key design decisions: a generic inbox_id field routes ownership between roles at each stage; stage_id drives the workflow state machine; a session field scopes submissions per cycle. The schema was normalized to eliminate redundancy while preserving audit trails via timestamps.",
            icon: "fas fa-database",
          },
          {
            number: "03",
            title: "Mobile-First Wireframes",
            description:
              "Wireframes began on mobile as a deliberate constraint — if the flow works on a small screen, it works everywhere. Login, sign-up, and authentication screens were sketched first to establish the entry point. Chevron-style navigation was chosen for multi-step application forms (Course Details → Applicant Details → Itinerary → Budget → Summary) to give users clear progress context at every step.",
            icon: "fas fa-mobile-alt",
          },
          {
            number: "04",
            title: "Dashboard Mock-ups",
            description:
              "Six dashboard screens were designed covering: user profile editing, user list management, course editing, course student assignment, and program creation. Each screen was mapped to a specific role permission level, ensuring the UI reflected the underlying authorization model rather than exposing functionality users cannot act upon.",
            icon: "fas fa-th-large",
          },
          {
            number: "05",
            title: "Workflow & Acceptance Criteria",
            description:
              "The approval workflow was modeled as a one-page scrolling layout showing all stages simultaneously, giving administrators a bird's-eye view of every application in the pipeline. Acceptance criteria were documented per stage including: complete cycle drafting, email notification mock-ups, KPI dashboards with metrics per user, and printable report formats — bridging the gap between business expectations and developer deliverables.",
            icon: "fas fa-tasks",
          },
        ],
        architecture: {
          stack: "PHP LAMP (Linux, Apache, MySQL, PHP) · Laravel Framework",
          entities: "19 Data Entities",
          keyEntities: [
            {
              name: "Application",
              note: "Central entity — holds status, stage, inbox owner, course, session",
            },
            {
              name: "Users",
              note: "Multi-role with institution and program foreign keys",
            },
            {
              name: "Stages",
              note: "Drives the workflow state machine from draft to final approval",
            },
            {
              name: "Budget",
              note: "Linked to application and expense types with a defined funding cap",
            },
            {
              name: "Itinerary",
              note: "Stores destination and travel schedule per application",
            },
          ],
          auth: "Role-based authorization with login / sign-up flows (OAuth2 pattern)",
        },
        deliverables: [
          "Stage decomposition diagram",
          "19-entity ERD with full attribute definitions",
          "Mobile-first login & sign-up wireframes",
          "6 dashboard screens (user, course, program management)",
          "5-step Chevron application input screens",
          "Workflow review — one-page scrolling layout",
          "Email notification mock-up",
          "KPI dashboard with printable metrics",
          "Acceptance criteria documentation per stage",
        ],
        valueProposition: [
          {
            title: "Robust Database Architecture",
            body: "A well-designed data model ensures accurate information representation and provides the foundation for reliable analytics, reporting, and decision-making.",
          },
          {
            title: "Clear Process Abstraction",
            body: "Breaking the process into named stages provides a structured framework for users and eliminates ambiguity between developer intent and business expectation.",
          },
          {
            title: "Intuitive, Responsive UX",
            body: "Mobile-first wireframes with Chevron navigation simplify complex multi-step workflows, improving user engagement across all device sizes.",
          },
        ],
      },
    },
    {
      title: "Detailed Project 1",
      type: types.DOCUMENT,
      document: {
        projectInfo:
          "Quidam lisque persius interesset his et, in quot quidam persequeris vim, ad mea essent possim iriure. Lisque persius interesset his et, in quot quidam persequeris vim, ad mea essent possim iriure.",
        client: "Ruby Clinton",
        technologies: "iOS, HTML5, CSS3, PHP, Java",
        industry: "Art & Design",
        date: "July 16, 2019",
        url: {
          name: "www.example.com",
          link: "https://www.example.com",
        },
        sliderImages: [
          "images/projects/project-2.jpg",
          "images/projects/project-5.jpg",
        ],
      },
      thumbImage: "images/projects/project-1.jpg",
      categories: [filters.DETAILED],
    },
    {
      title: "Mockups Design 1",
      type: types.IMAGE,
      thumbImage: "images/projects/project-2.jpg",
      categories: [filters.MOCKUPS],
    },
    {
      title: "YouTube Video",
      type: types.VIDEO,
      video: { vimeo: false, id: "PMNnEEEacCg" },
      thumbImage: "images/projects/project-3.jpg",
      categories: [filters.YOUTUBE],
    },
    {
      title: "Vimeo Video",
      type: types.VIDEO,
      video: { vimeo: true, id: "259411563" },
      thumbImage: "images/projects/project-4.jpg",
      categories: [filters.VIMEO],
    },
    {
      title: "Detailed Project 2",
      type: types.DOCUMENT,
      document: {
        projectInfo:
          "Quidam lisque persius interesset his et, in quot quidam persequeris vim, ad mea essent possim iriure. Lisque persius interesset his et, in quot quidam persequeris vim, ad mea essent possim iriure.",
        client: "Ruby Clinton",
        technologies: "iOS, HTML5, CSS3, PHP, Java",
        industry: "Art & Design",
        date: "July 16, 2019",
        url: {
          name: "www.example.com",
          link: "https://www.example.com",
        },
        sliderImages: [
          "images/projects/project-1.jpg",
          "images/projects/project-2.jpg",
        ],
      },
      thumbImage: "images/projects/project-5.jpg",
      categories: [filters.DETAILED],
    },
    {
      title: "Mockups Design 2",
      type: types.IMAGE,
      thumbImage: "images/projects/project-6.jpg",
      categories: [filters.MOCKUPS],
    },
  ];

  useEffect(() => {
    isotope.current = new Isotope(".portfolio-filter", {
      itemSelector: ".filter-item",
      layoutMode: "masonry",
      originLeft: !isRtl,
    });
    return () => isotope.current.destroy();
  }, []);

  useEffect(() => {
    if (imagesLoaded) {
      filterKey === "*"
        ? isotope.current.arrange({ filter: `*` })
        : isotope.current.arrange({ filter: `.${filterKey}` });
    }
  }, [filterKey, imagesLoaded]);

  const handleFilterKeyChange = (key) => () => setFilterKey(key);

  const getKeyByValue = (value) =>
    Object.keys(filters).find((key) => filters[key] === value);

  const getFilterClasses = (categories) => {
    if (categories.length > 0) {
      return categories.map((cat) => getKeyByValue(cat)).join(" ");
    }
  };

  return (
    <>
      <section id="portfolio" className="section bg-light">
        <div className="container">
          <p className="text-center mb-2 wow fadeInUp">
            <span className="bg-primary text-dark px-2">Portfolio</span>
          </p>
          <h2 className="text-10 fw-600 text-center mb-5 wow fadeInUp">
            Some of my most recent projects
          </h2>

          {/* Filter Menu */}
          <ul className="portfolio-menu nav nav-tabs fw-600 justify-content-start justify-content-md-center border-bottom-0 mb-5 wow fadeInUp">
            <li className="nav-item">
              <button
                className={"nav-link " + (filterKey === "*" ? "active" : "")}
                onClick={handleFilterKeyChange("*")}
              >
                All
              </button>
            </li>
            {Object.keys(filters).map((oneKey, i) => (
              <li className="nav-item" key={i}>
                <button
                  className={
                    "nav-link " + (filterKey === oneKey ? "active" : "")
                  }
                  onClick={handleFilterKeyChange(oneKey)}
                >
                  {filters[oneKey]}
                </button>
              </li>
            ))}
          </ul>

          <div className="portfolio wow fadeInUp">
            <div className="row portfolio-filter filter-container g-4">
              {projectsData.map((project, index) => (
                <div
                  className={
                    "col-sm-6 col-lg-4 filter-item " +
                    getFilterClasses(project.categories)
                  }
                  key={index}
                  style={{ position: "relative" }}
                >
                  <div className="portfolio-box">
                    {project.type === types.CASESTUDY && (
                      <div
                        style={{
                          position: "absolute",
                          top: "12px",
                          left: "12px",
                          zIndex: 2,
                          background: "#000",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: "700",
                          letterSpacing: "1.5px",
                          textTransform: "uppercase",
                          padding: "4px 10px",
                          pointerEvents: "none",
                        }}
                      >
                        Case Study
                      </div>
                    )}
                    <div className="portfolio-img">
                      <img
                        onLoad={() => setimagesLoaded(imagesLoaded + 1)}
                        className="img-fluid d-block portfolio-image"
                        src={project.thumbImage}
                        alt={project.title}
                      />
                      <div
                        className="portfolio-overlay"
                        onClick={() => {
                          setSelectedProjectDetails(projectsData[index]);
                          setIsOpen(true);
                        }}
                      >
                        <button className="popup-ajax stretched-link border-0 p-0">
                          {" "}
                        </button>
                        <div className="portfolio-overlay-details">
                          <p className="text-primary text-8">
                            {project.type === types.CASESTUDY && (
                              <i className="fas fa-layer-group"></i>
                            )}
                            {project.type === types.DOCUMENT && (
                              <i className="fas fa-file-alt"></i>
                            )}
                            {project.type === types.IMAGE && (
                              <i className="fas fa-image"></i>
                            )}
                            {project.type === types.VIDEO && (
                              <i className="fas fa-video"></i>
                            )}
                          </p>
                          <h5 className="text-white text-5">{project?.title}</h5>
                          {project?.subtitle && (
                            <span className="text-light" style={{ fontSize: "11px" }}>
                              {project.subtitle}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {isOpen && (
        <ProjectDetailsModal
          projectDetails={selectedProjectDetails}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  );
};

export default Portfolio;
